import React, { useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  Circle,
  Polyline,
  Polygon,
  LayerGroup,
  LayersControl,
  Tooltip as LeafletTooltip,
  useMap,
} from 'react-leaflet';
import L, { LatLng } from 'leaflet';
import 'leaflet/dist/leaflet.css';

import { VisitLogEntry } from '../../../../types/VisitLogEntry';
import { VisitGeofence, GEOFENCE_COLORS } from '../../../../hooks/useVisitGeofences';

import useDateTimeFormat from '../../../../hooks/useDateTimeFormat';
import VisitMapLegend from '../../../maps/components/VisitMapLegend';

// Styled
const MapWrapper = styled(Box)({
  height: '100%',
  minHeight: 580,
  '& .leaflet-container': { height: '100%', width: '100%', borderRadius: 0 },
});

// Types
export interface UniqueReader {
  lat: number;
  lng: number;
  name: string;
  imei: string;
}

export interface UniqueFacility {
  lat: number;
  lng: number;
  name: string;
  bounds: [number, number][];
}

interface VisitJourneyMapProps {
  filtered: VisitLogEntry[];
  exactCoords: [number, number][];
  spreadPositions: [number, number][];
  center: LatLng;
  activePosition: LatLng | null;
  activeIdx: number | null;
  hoveredIdx: number | null;
  uniqueReaders: UniqueReader[];
  uniqueFacilities: UniqueFacility[];
  geofences?: VisitGeofence[];
  onMarkerClick: (i: number) => void;
  onMarkerHover: (i: number | null) => void;
}

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Icons
const readerPinIcon = L.divIcon({
  className: '',
  html: `<div style="filter:drop-shadow(0 2px 5px rgba(0,0,0,0.35))">
    <svg width="24" height="32" viewBox="0 0 24 32" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 0C5.4 0 0 5.4 0 12C0 21 12 32 12 32S24 21 24 12C24 5.4 18.6 0 12 0Z" fill="#7c3aed"/>
      <circle cx="12" cy="12" r="5" fill="white"/>
      <circle cx="12" cy="12" r="2.5" fill="#7c3aed"/>
    </svg>
  </div>`,
  iconSize: [24, 32],
  iconAnchor: [12, 32],
  popupAnchor: [0, -34],
});

const makeSightingIcon = (index: number, isActive: boolean, isHovered: boolean) =>
  L.divIcon({
    className: '',
    html: `<div style="
      width:20px;height:20px;border-radius:50%;
      background:${isActive ? '#7c3aed' : isHovered ? '#a78bfa' : '#c4b5fd'};
      border:2px solid ${isActive ? '#4c1d95' : '#7c3aed'};
      display:flex;align-items:center;justify-content:center;
      font-size:9px;font-weight:700;color:#fff;
      box-shadow:0 1px 4px rgba(0,0,0,${isActive ? '0.4' : isHovered ? '0.35' : '0.2'});
      transform:${isActive ? 'scale(1.35)' : isHovered ? 'scale(1.2)' : 'scale(1)'};
      transition:transform 0.15s;
    ">${index}</div>`,
    iconSize: [20, 20],
    iconAnchor: [10, 10],
    popupAnchor: [0, -12],
  });

const startFlagIcon = L.divIcon({
  className: '',
  html: `<div style="filter:drop-shadow(0 2px 4px rgba(0,0,0,0.3))">
    <svg width="28" height="36" viewBox="0 0 28 36" xmlns="http://www.w3.org/2000/svg">
      <line x1="4" y1="2" x2="4" y2="34" stroke="#15803d" stroke-width="2.5" stroke-linecap="round"/>
      <polygon points="4,2 24,8 4,14" fill="#16a34a" stroke="#15803d" stroke-width="1"/>
      <text x="8" y="12" font-size="6" fill="white" font-family="sans-serif" font-weight="700">S</text>
    </svg>
  </div>`,
  iconSize: [28, 36],
  iconAnchor: [4, 34],
  popupAnchor: [10, -34],
});

const endFlagIcon = L.divIcon({
  className: '',
  html: `<div style="filter:drop-shadow(0 2px 4px rgba(0,0,0,0.3))">
    <svg width="28" height="36" viewBox="0 0 28 36" xmlns="http://www.w3.org/2000/svg">
      <line x1="4" y1="2" x2="4" y2="34" stroke="#b91c1c" stroke-width="2.5" stroke-linecap="round"/>
      <polygon points="4,2 24,8 4,14" fill="#dc2626" stroke="#b91c1c" stroke-width="1"/>
      <text x="8" y="12" font-size="6" fill="white" font-family="sans-serif" font-weight="700">E</text>
    </svg>
  </div>`,
  iconSize: [28, 36],
  iconAnchor: [4, 34],
  popupAnchor: [10, -34],
});

// Map control helpers
const FitBounds: React.FC<{ coords: [number, number][] }> = ({ coords }) => {
  const map = useMap();
  useEffect(() => {
    if (coords.length > 0) {
      const bounds = L.latLngBounds(coords.map(([lat, lng]) => L.latLng(lat, lng)));
      map.fitBounds(bounds, { padding: [60, 60], maxZoom: 18 });
    }
  }, [coords, map]);
  return null;
};

const PanTo: React.FC<{ position: LatLng | null }> = ({ position }) => {
  const map = useMap();
  useEffect(() => {
    if (position) map.setView(position, Math.max(map.getZoom(), 18), { animate: true });
  }, [position, map]);
  return null;
};

const ResetView: React.FC<{ coords: [number, number][] }> = ({ coords }) => {
  const map = useMap();
  const handleReset = () => {
    if (coords.length > 0) {
      const bounds = L.latLngBounds(coords.map(([lat, lng]) => L.latLng(lat, lng)));
      map.fitBounds(bounds, { padding: [60, 60], maxZoom: 18, animate: true });
    }
  };
  return (
    <div className='leaflet-top leaflet-left' style={{ marginTop: 80, pointerEvents: 'auto' }}>
      <div className='leaflet-control leaflet-bar'>
        <a
          href='#'
          title='Reset to full view'
          role='button'
          aria-label='Reset to full view'
          onClick={(e) => {
            e.preventDefault();
            handleReset();
          }}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 26, height: 26, color: '#444', textDecoration: 'none' }}
        >
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='14'
            height='14'
            viewBox='0 0 24 24'
            fill='none'
            stroke='currentColor'
            strokeWidth='2.5'
            strokeLinecap='round'
            strokeLinejoin='round'
          >
            <path d='M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8' />
            <path d='M3 3v5h5' />
          </svg>
        </a>
      </div>
    </div>
  );
};

const rssiLabel = (rssi: number): string => {
  if (rssi >= -60) return 'Strong';
  if (rssi >= -75) return 'Fair';
  return 'Weak';
};

export default function VisitJourneyMap({
  filtered,
  exactCoords,
  spreadPositions,
  center,
  activePosition,
  activeIdx,
  hoveredIdx,
  uniqueReaders,
  uniqueFacilities,
  geofences = [],
  onMarkerClick,
  onMarkerHover,
}: VisitJourneyMapProps) {
  const { toStandardDate, withTimeMode, timeModeName } = useDateTimeFormat();
  const lastIdx = filtered.length - 1;

  return (
    <MapWrapper>
      {filtered.length > 0 ? (
        <MapContainer center={center} zoom={17} scrollWheelZoom zoomControl attributionControl style={{ height: '100%', width: '100%' }}>
          <LayersControl position='topright'>
            <LayersControl.BaseLayer checked name='Street'>
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
              />
            </LayersControl.BaseLayer>
            <LayersControl.BaseLayer name='Satellite'>
              <TileLayer
                attribution='&copy; Esri'
                url='https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
              />
            </LayersControl.BaseLayer>
          </LayersControl>

          <FitBounds coords={exactCoords} />
          <PanTo position={activePosition} />
          <ResetView coords={exactCoords} />

          <LayerGroup>
            {/* Real facility geofences (colour-tiered): green = most recent closest facility,
                yellow = part of the closest-facility journey, purple = standard facility.
                Falls back to the legacy square bounds when no geofences are provided. */}
            {geofences.length > 0
              ? geofences.map((geo, gi) => {
                  const stroke = GEOFENCE_COLORS[geo.tier];
                  const isHighlight = geo.tier !== 'purple';
                  return (
                    <Polygon
                      key={`geo-${geo.facilityId || gi}-${gi}`}
                      positions={geo.positions}
                      pathOptions={{
                        color: stroke,
                        fillColor: stroke,
                        fillOpacity: isHighlight ? 0.18 : 0.08,
                        weight: isHighlight ? 3 : 2,
                        dashArray: geo.tier === 'green' ? undefined : '6 2',
                      }}
                    >
                      <Popup>
                        <Typography variant='caption' fontWeight={700} color='#4c1d95' display='block'>
                          {geo.facilityName}
                        </Typography>
                        <Typography variant='caption' color='text.secondary'>
                          {geo.tier === 'green' ? 'Most recent closest facility' : geo.tier === 'yellow' ? 'Closest facility (journey)' : 'Facility'}
                        </Typography>
                      </Popup>
                    </Polygon>
                  );
                })
              : uniqueFacilities.map((fac, fi) => (
                  <Polygon
                    key={`fac-${fi}`}
                    positions={fac.bounds as [number, number][]}
                    pathOptions={{ color: 'purple', fillColor: 'purple', fillOpacity: 0.07, weight: 2, dashArray: '6 4' }}
                  >
                    <Popup>
                      <Typography variant='caption' fontWeight={700} color='#4c1d95' display='block'>
                        {fac.name}
                      </Typography>
                      <Typography variant='caption' color='text.secondary'>
                        Facility boundary
                      </Typography>
                    </Popup>
                  </Polygon>
                ))}

            {/* Reader range circles */}
            {uniqueReaders.map((rdr, ri) => (
              <Circle
                key={`rdr-circle-${ri}`}
                center={[rdr.lat, rdr.lng]}
                radius={50}
                pathOptions={{ color: '#9ca3af', fillColor: '#9ca3af', fillOpacity: 0.06, weight: 1.5, dashArray: '5 4' }}
              />
            ))}

            {/* Reader pins */}
            {uniqueReaders.map((rdr, ri) => (
              <Marker key={`rdr-pin-${ri}`} position={[rdr.lat, rdr.lng]} icon={readerPinIcon} zIndexOffset={500}>
                <Popup>
                  <Typography variant='caption' fontWeight={700} color='#4c1d95' display='block'>
                    {rdr.name}
                  </Typography>
                  <Typography variant='caption' color='text.secondary'>
                    IMEI: {rdr.imei}
                  </Typography>
                </Popup>
              </Marker>
            ))}

            {/* Yellow journey polyline */}
            {spreadPositions.length > 1 && (
              <Polyline positions={spreadPositions} pathOptions={{ color: '#eab308', weight: 2.5, dashArray: '6 5', opacity: 0.9 }} />
            )}

            {/* Middle sighting dots */}
            {filtered.map((entry, i) => {
              if (i === 0 || i === lastIdx) return null;
              const pos = spreadPositions[i];
              if (!pos) return null;
              const label = entry.stepNumber ?? i + 1;
              return (
                <Marker
                  key={entry.id}
                  position={pos}
                  icon={makeSightingIcon(label, activeIdx === i, hoveredIdx === i)}
                  zIndexOffset={activeIdx === i ? 1000 : hoveredIdx === i ? 500 : 100}
                  eventHandlers={{ click: () => onMarkerClick(i), mouseover: () => onMarkerHover(i), mouseout: () => onMarkerHover(null) }}
                >
                  <LeafletTooltip direction='top' offset={[0, -10]}>
                    <Typography variant='caption' display='block' fontWeight={600}>
                      Sighting #{label}
                    </Typography>
                    <Typography variant='caption' display='block'>
                      {toStandardDate(entry.firstSeenDateTime)} → {toStandardDate(entry.lastSeenDateTime)} ({timeModeName})
                    </Typography>
                    <Typography variant='caption' display='block'>
                      RSSI: {entry.rssi} dBm · {entry.temperatureF}°F
                    </Typography>
                  </LeafletTooltip>
                  <Popup>
                    <Typography variant='caption' fontWeight={700} display='block'>
                      Sighting #{label}
                    </Typography>
                    <Typography variant='caption' display='block'>
                      {withTimeMode('First')}: {toStandardDate(entry.firstSeenDateTime)}
                    </Typography>
                    <Typography variant='caption' display='block'>
                      {withTimeMode('Last')}: {toStandardDate(entry.lastSeenDateTime)}
                    </Typography>
                    <Typography variant='caption' display='block'>
                      Temp: {entry.temperatureF}°F · Humidity: {entry.humidity}%
                    </Typography>
                    <Typography variant='caption' display='block'>
                      Signal: {entry.rssi} dBm ({rssiLabel(entry.rssi)})
                    </Typography>
                  </Popup>
                </Marker>
              );
            })}

            {/* Start flag */}
            {filtered.length > 0 && (
              <Marker
                key={`start-${filtered[0].id}`}
                position={[filtered[0].location.latitude, filtered[0].location.longitude]}
                icon={startFlagIcon}
                zIndexOffset={activeIdx === 0 ? 2000 : hoveredIdx === 0 ? 1500 : 900}
                eventHandlers={{ click: () => onMarkerClick(0), mouseover: () => onMarkerHover(0), mouseout: () => onMarkerHover(null) }}
              >
                <LeafletTooltip direction='top' offset={[10, -30]}>
                  <Typography variant='caption' display='block' fontWeight={600} sx={{ color: '#15803d' }}>
                    Start — Sighting #{filtered[0].stepNumber ?? 1}
                  </Typography>
                  <Typography variant='caption' display='block'>
                    {toStandardDate(filtered[0].firstSeenDateTime)} ({timeModeName})
                  </Typography>
                </LeafletTooltip>
                <Popup>
                  <Typography variant='caption' fontWeight={700} display='block' sx={{ color: '#15803d' }}>
                    Start — Sighting #{filtered[0].stepNumber ?? 1}
                  </Typography>
                  <Typography variant='caption' display='block'>
                    {withTimeMode('First')}: {toStandardDate(filtered[0].firstSeenDateTime)}
                  </Typography>
                  <Typography variant='caption' display='block'>
                    {withTimeMode('Last')}: {toStandardDate(filtered[0].lastSeenDateTime)}
                  </Typography>
                  <Typography variant='caption' display='block'>
                    Temp: {filtered[0].temperatureF}°F · Humidity: {filtered[0].humidity}%
                  </Typography>
                  <Typography variant='caption' display='block'>
                    Signal: {filtered[0].rssi} dBm ({rssiLabel(filtered[0].rssi)})
                  </Typography>
                </Popup>
              </Marker>
            )}

            {/* End flag */}
            {filtered.length > 1 && (
              <Marker
                key={`end-${filtered[lastIdx].id}`}
                position={[filtered[lastIdx].location.latitude, filtered[lastIdx].location.longitude]}
                icon={endFlagIcon}
                zIndexOffset={activeIdx === lastIdx ? 2000 : hoveredIdx === lastIdx ? 1500 : 800}
                eventHandlers={{ click: () => onMarkerClick(lastIdx), mouseover: () => onMarkerHover(lastIdx), mouseout: () => onMarkerHover(null) }}
              >
                <LeafletTooltip direction='top' offset={[10, -30]}>
                  <Typography variant='caption' display='block' fontWeight={600} sx={{ color: '#b91c1c' }}>
                    End — Sighting #{filtered[lastIdx].stepNumber ?? filtered.length}
                  </Typography>
                  <Typography variant='caption' display='block'>
                    {toStandardDate(filtered[lastIdx].firstSeenDateTime)} ({timeModeName})
                  </Typography>
                </LeafletTooltip>
                <Popup>
                  <Typography variant='caption' fontWeight={700} display='block' sx={{ color: '#b91c1c' }}>
                    End — Sighting #{filtered[lastIdx].stepNumber ?? filtered.length}
                  </Typography>
                  <Typography variant='caption' display='block'>
                    {withTimeMode('First')}: {toStandardDate(filtered[lastIdx].firstSeenDateTime)}
                  </Typography>
                  <Typography variant='caption' display='block'>
                    {withTimeMode('Last')}: {toStandardDate(filtered[lastIdx].lastSeenDateTime)}
                  </Typography>
                  <Typography variant='caption' display='block'>
                    Temp: {filtered[lastIdx].temperatureF}°F · Humidity: {filtered[lastIdx].humidity}%
                  </Typography>
                  <Typography variant='caption' display='block'>
                    Signal: {filtered[lastIdx].rssi} dBm ({rssiLabel(filtered[lastIdx].rssi)})
                  </Typography>
                </Popup>
              </Marker>
            )}
          </LayerGroup>

          <VisitMapLegend />
        </MapContainer>
      ) : (
        <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant='body2' color='text.secondary'>
            No sightings found for this visit.
          </Typography>
        </Box>
      )}
    </MapWrapper>
  );
}
